import Immutable from 'immutable';

import TablePreferencesActions from 'sharedreact/actions/TablePreferencesActions';
import UserPreferencesActions from 'sharedreact/actions/UserPreferencesActions';
import AuthenticationConnection from 'sharedreact/connections/AuthenticationConnection';
import AuthenticationConstants from 'sharedreact/constants/AuthenticationConstants';
import SessionWarningMode from 'sharedreact/constants/SessionWarningMode';
import type Authentication from 'sharedreact/data/Authentication';
import AuthenticationMethod from 'sharedreact/data/AuthenticationMethod';
import ErrorData from 'sharedreact/data/ErrorData';
import type User from 'sharedreact/data/User';
import type UserWithPassword from 'sharedreact/data/UserWithPassword';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';
import TablePreferencesStore from 'sharedreact/stores/TablePreferencesStore';

function _onLoginSuccess (
  user: User,
  sessionTimeout?: number,
  maxExtendTimes?: number,
  locations?: Immutable.List<string>,
  postLogin?: () => void
): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.LOGIN_SUCCESS,
    item: user,
    sessionTimeout: sessionTimeout,
    maxExtendTimes: maxExtendTimes,
    locations: locations,
    logLevel: 'debug'
  });

  if (postLogin) {
    postLogin();
  }
}

function _onReloginSuccess (
  user: User,
  sessionTimeout?: number,
  maxExtendTimes?: number,
  locations?: Immutable.List<string>,
  postLogin?: () => void
): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.LOGIN_SUCCESS,
    item: user,
    sessionTimeout: sessionTimeout,
    maxExtendTimes: maxExtendTimes,
    locations: locations,
    isRelogin: true,
    logLevel: 'debug'
  });

  if (postLogin) {
    postLogin();
  }
}

function _onLoginFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.LOGIN_FAIL,
    error: error,
    logLevel: 'error'
  });
}

function _onReloginFailure (): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.RELOGIN_FAIL,
    logLevel: 'debug'
  });
}

function _onLogoutSuccess (postLogout?: () => void): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.LOGOUT_SUCCESS,
    logLevel: 'debug'
  });

  if (postLogout) {
    postLogout();
  }
}

function _onLogoutFailure (error: ErrorData): void {
  console.log('Failed to logout: ' + error.message);
}

function _getLogonMethodsSuccess (authenticationMethods: Immutable.List<AuthenticationMethod>): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.GET_LOGON_METHODS_SUCCESS,
    authenticationMethods: authenticationMethods,
    logLevel: 'debug'
  });
}

function _getLogonMethodsFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.GET_LOGON_METHODS_FAIL,
    error: error,
    logLevel: 'debug'
  });
}

function _onForgotPasswordSuccess (nrOfMailsSent: number): void {
  if (nrOfMailsSent > 0) {
    AppDispatcher.dispatch({
      actionType: AuthenticationConstants.FORGOT_PASSWORD_SUCCESS,
      logLevel: 'debug'
    });
  } else {
    _onForgotPasswordFailure(new ErrorData({message: 'shared.authentication.error.sendemailfailed'}));
  }
}

function _onForgotPasswordFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.FORGOT_PASSWORD_FAIL,
    error: error,
    logLevel: 'error'
  });
}

function _onResetPasswordSuccess (user: User): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.RESET_PASSWORD_SUCCESS,
    item: user,
    logLevel: 'debug'
  });
}

function _onResetPasswordFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.RESET_PASSWORD_FAIL,
    error: error,
    logLevel: 'error'
  });
}

function _onChangePasswordSuccess (): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.CHANGE_PASSWORD_SUCCESS,
    logLevel: 'debug'
  });
}

function _onChangePasswordFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.CHANGE_PASSWORD_FAIL,
    error: error,
    logLevel: 'error'
  });
}

function _getSessionIdSuccess (sessionId: string): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.GET_SESSION_ID_SUCCESS,
    sessionId: sessionId,
    logLevel: 'debug'
  });
}

function _getSessionIdFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.GET_SESSION_ID_FAIL,
    error: error,
    logLevel: 'debug'
  });
}

// exported functions

function getLogonMethods (): void {
  AuthenticationConnection.getLogonMethods(_getLogonMethodsSuccess, _getLogonMethodsFailure);
}

function login (authentication: Authentication, postLogin?: () => void): void {
  AuthenticationConnection.doLogin(
    authentication.userName,
    authentication.password,
    authentication.version,
    (user, sessionTimeout, maxExtendTimes, locations) => _onLoginSuccess(
      user, sessionTimeout, maxExtendTimes, locations, postLogin
    ),
    _onLoginFailure
  );

  // dispatch the login action to the stores
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.LOGIN,
    logLevel: 'debug'
  });
}

function relogin (postLogin?: () => void): void {
  AuthenticationConnection.doRelogin((user, sessionTimeout, maxExtendTimes, locations) => _onReloginSuccess(
    user, sessionTimeout, maxExtendTimes, locations, postLogin), _onReloginFailure
  );

  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.RELOGIN,
    logLevel: 'debug'
  });
}

function logout (postLogout?: () => void): void {
  const handleSavePreferencesDone = () => AuthenticationConnection.doLogout(() => _onLogoutSuccess(postLogout), _onLogoutFailure);
  const handleSaveTablePreferencesDone = () => UserPreferencesActions.save(handleSavePreferencesDone, handleSavePreferencesDone);
  TablePreferencesActions.save(TablePreferencesStore.getState(), handleSaveTablePreferencesDone, handleSaveTablePreferencesDone);

  // dispatch the logout action to the stores
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.LOGOUT,
    logLevel: 'debug'
  });
}

function loginWithOkta (oktaLoginCode: string, idpName: string, postLogin?: () => void): void {
  AuthenticationConnection.doLoginWithOkta(
    oktaLoginCode,
    idpName,
    (user, sessionTimeout, maxExtendTimes, locations) => _onLoginSuccess(user, sessionTimeout, maxExtendTimes, locations, postLogin),
    _onLoginFailure
  );

  // dispatch the login action to the stores
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.LOGIN_WITH_OKTA,
    logLevel: 'debug'
  });
}

function signalOktaLoginFailure (errorTitle: string, errorMessage: string): void {
  const error: ErrorData = new ErrorData({
    status: 'error',
    message: errorTitle,
    info: errorMessage
  });

  _onLoginFailure(error);
}

function reloginWithId (sessionId: string, postLogin?: () => void, reloginFailed?: () => void): void {
  AuthenticationConnection.doReloginWithId(
    sessionId,
    (user, sessionTimeout, maxExtendTimes, locations) => _onLoginSuccess(user, sessionTimeout, maxExtendTimes, locations, postLogin),
    (error) => {
      if (reloginFailed != null) {
        reloginFailed();
      }
      _onLoginFailure(error);
    }
  );

  // dispatch the login action to the stores
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.LOGIN,
    logLevel: 'debug'
  });
}

// fake logout for native apps that behaves like a logout on client side but does not logout of AE
function logoutForId (postLogout?: () => void): void {
  const handleSavePreferencesDone = () => _onLogoutSuccess(postLogout);
  const handleSaveTablePreferencesDone = () => UserPreferencesActions.save(handleSavePreferencesDone, handleSavePreferencesDone);
  TablePreferencesActions.save(TablePreferencesStore.getState(), handleSaveTablePreferencesDone, handleSaveTablePreferencesDone);

  // dispatch the logout action to the stores
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.LOGOUT,
    logLevel: 'debug'
  });
}

function forgotPassword (authentication: Authentication, language: string): void {
  AuthenticationConnection.doForgotPassword(
    authentication.userName,
    language,
    _onForgotPasswordSuccess,
    _onForgotPasswordFailure
  );

  // dispatch the forgot password action to the stores
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.FORGOT_PASSWORD,
    logLevel: 'debug'
  });
}

function clearForgotPasswordMailHasBeenSent (): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.CLEAR_FORGOT_PASSWORD_EMAIL_SENT,
    logLevel: 'debug'
  });
}

function resetPassword (authentication: Authentication, identifier: string, onSuccess?: () => void): void {
  AuthenticationConnection.doResetPassword(
    authentication.password,
    identifier,
    (user: User) => {
      _onResetPasswordSuccess(user);
      if (onSuccess != null) onSuccess();
    },
    _onResetPasswordFailure
  );

  // dispatch the reset password action to the stores
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.RESET_PASSWORD,
    logLevel: 'debug'
  });
}

function changePassword (currentUserWithPassword: UserWithPassword, newPassword: string): void {
  AuthenticationConnection.doChangePassword(
    currentUserWithPassword,
    newPassword,
    _onChangePasswordSuccess,
    _onChangePasswordFailure
  );

  // dispatch the change password action to the stores
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.CHANGE_PASSWORD,
    logLevel: 'debug'
  });
}

function resetChangePasswordRequest (): void {
  // dispatch the rest change password request action to the stores
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.CHANGE_PASSWORD_RESET,
    logLevel: 'debug'
  });
}

function goToLoginPage (postLogout?: () => void): void {
  _onLogoutSuccess(postLogout);
}

function handleUnauthorized (postLogout: () => void): void {
  postLogout();

  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.SET_SESSION_WARNING_MODE,
    mode: SessionWarningMode.Expired,
    logLevel: 'trace'
  });
}

function clearWarningMode (): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.SET_SESSION_WARNING_MODE,
    mode: SessionWarningMode.NoWarning,
    logLevel: 'trace'
  });
}

function extendSession (): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.EXTEND_SESSION,
    logLevel: 'debug'
  });
}

function setWillExpire(): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.SET_SESSION_WARNING_MODE,
    mode: SessionWarningMode.WillExpire,
    logLevel: 'trace'
  });
}

function handleClosedSession (): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.LOGOUT_SUCCESS,
    logLevel: 'trace'
  });
}

function getSessionId (): void {
  AuthenticationConnection.getSessionId(_getSessionIdSuccess, _getSessionIdFailure);
}

function toggleSlideOut (): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.TOGGLE_SLIDE_OUT,
    logLevel: 'trace'
  });
}

function hideSlideOut (): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.HIDE_SLIDE_OUT,
    logLevel: 'trace'
  });
}

function _getServerVersionSuccess (serverVersion: string): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.GET_SERVER_VERSION_SUCCESS,
    serverVersion: serverVersion,
    logLevel: 'trace'
  });
}

function _getServerVersionFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: AuthenticationConstants.GET_SERVER_VERSION_FAIL,
    error: error,
    logLevel: 'error'
  });
}

function getServerVersion (): void {
  AuthenticationConnection.getServerVersion(_getServerVersionSuccess, _getServerVersionFailure);
}

export default {
  getLogonMethods,
  login,
  relogin,
  logout,
  loginWithOkta,
  signalOktaLoginFailure,
  reloginWithId,
  logoutForId,
  forgotPassword,
  resetPassword,
  changePassword,
  resetChangePasswordRequest,
  goToLoginPage,
  handleUnauthorized,
  clearWarningMode,
  extendSession,
  setWillExpire,
  handleClosedSession,
  getSessionId,
  toggleSlideOut,
  hideSlideOut,
  clearForgotPasswordMailHasBeenSent,
  getServerVersion
};
